import React, { useState, useRef, useEffect, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { routesConfig } from "../routesConfig";
import {
  SquaresFour,
  Certificate,
  PhoneX,
  Notebook,
  HandHeart,
  VideoConference,
  Envelope,
  Basket,
  Church,
  HandsPraying,
  Invoice,
  BuildingOffice,
  SignOut,
  House,
  ChartDonut,
  PhoneDisconnect,
  PersonSimpleRun,
  BookOpenText,
  MapPinArea,
  ClipboardText,
  CaretRight,
  UserSound,
  PhoneSlash,
  FolderUser,
  FolderStar,
  FolderPlus,
  FolderDashed,
  Equalizer,
  UserRectangle,
  SealCheck,
  KeyReturn,
  ChartBarHorizontal,
  CalendarHeart,
  Voicemail,
    Headset,
    Users,
    Gear,
    UserCircle
} from "@phosphor-icons/react";
import { ProfileContext } from "../contexts/ProfileContext";
import { OrganizationContext } from "../contexts/OrganizationContext";
import { Tooltip } from "react-tooltip";
import "../styles/Sidemenu-new.css";

// JSON Configuration for custom labels
const CUSTOM_LABELS = {
  "/resources": "Mailing",
  "/pastoral-care/completed-follow-ups": "completed follow-ups",
  "/pastoral-care/new-follow-ups": "new follow-ups",
  "/pastoral-care/ongoing-follow-ups": "ongoing follow-ups",
  "/resources/partner": "Prayer partner",
};

const SideMenu = ({ userRole, userName, userImage, onToggle }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openSubSubmenu, setOpenSubSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const [subSubmenuPosition, setSubSubmenuPosition] = useState({
    top: 0,
    left: 0,
  });

  const {
    handleShow,
    worker,
    getCurrentOrganization,
    setOrganizationNumber,
    setViewingAsOrganization,
  } = useContext(ProfileContext);
  const { nameClicked, setNameClicked } = useContext(OrganizationContext);
  const location = useLocation();
  const activePath = location.pathname;
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const submenuRef = useRef(null);
  const subSubmenuRef = useRef(null);
  const { logout } = useAuth0();

  useEffect(() => {
    if (!userRole || !userName) {
      console.warn(
        "SideMenu: userRole or userName is missing, logging out user",
      );
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  }, [userRole, userName, logout]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside all menu elements
      const isOutsideSidebar =
        sidebarRef.current && !sidebarRef.current.contains(event.target);
      const isOutsideSubmenu =
        submenuRef.current && !submenuRef.current.contains(event.target);
      const isOutsideSubSubmenu =
        subSubmenuRef.current && !subSubmenuRef.current.contains(event.target);

      // If sub-submenu is open, check if click is outside both submenu and sub-submenu
      if (openSubSubmenu) {
        if (isOutsideSubmenu && isOutsideSubSubmenu) {
          setOpenSubmenu(null);
          setOpenSubSubmenu(null);
        }
      }
      // If only submenu is open, check if click is outside both sidebar and submenu
      else if (openSubmenu) {
        if (isOutsideSidebar && isOutsideSubmenu) {
          setOpenSubmenu(null);
          setOpenSubSubmenu(null);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openSubmenu, openSubSubmenu]); // Add dependencies to ensure latest state

  if (!userRole || !userName) {
    return null;
  }

  const allowedRoutes = routesConfig.filter(
    (route) =>
      route && route.allowedRoles && route.allowedRoles.includes(userRole),
  );

  const getLabel = (path) => {
    if (CUSTOM_LABELS[path]) {
      return CUSTOM_LABELS[path].toUpperCase();
    }
    const defaultLabel = path.split("/").pop().replace(/-/g, " ").toUpperCase();
    return defaultLabel || "UNKNOWN";
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleHomeClick = () => {
    setOpenSubmenu(null);
    setOpenSubSubmenu(null);

    // ✅ Set viewingAsOrganization to false when going home
    setViewingAsOrganization(false);

    if (worker) {
      getCurrentOrganization(worker).then((foundOrganization) => {
        if (foundOrganization) {
          if (
            worker.role === "super_admin" ||
            worker.role === "admin" ||
            worker.role === "prayer_centre_admin" ||
            worker.role === "prayer_centre_lead" ||
            worker.role === "account_manager" 
          ) {
            setOrganizationNumber(foundOrganization.assignedNumber);
          }
        }
      });
    }
    setNameClicked("");
    navigate("/home");
  };

  const getIcon = (path, defaultPath = "") => {
    const iconProps = { size: 24, weight: "regular" };

      switch (path) {
      case "/account":
              return <UserCircle {...iconProps} />;
      case "/settings":
              return <Gear {...iconProps} />;
      case "/users":
              return <Users {...iconProps} />;
      case "/home":
        return <SquaresFour {...iconProps} />;
      case "/reports":
        return <ChartDonut {...iconProps} />;
      case "/reports/types-of-calls":
        return <PhoneDisconnect {...iconProps} />;
      case "/reports/age-+-gender":
        return <PersonSimpleRun {...iconProps} />;
      case "/reports/resource":
        return <BookOpenText {...iconProps} />;
      case "/reports/calls-by-area-codes":
        return <MapPinArea {...iconProps} />;
      case "/reports/prayer-partner-hours":
        return <Headset size={24} strokeWidth={1.5} />;
      case "/reports/disposition":
        return <ClipboardText {...iconProps} />;
      case "/reports/requests":
        return <CalendarHeart {...iconProps} />;
      case "/reports/requests/prayer-requests":
        return <Basket {...iconProps} />;
      case "/reports/requests/voicemail":
        return <Voicemail {...iconProps} />;
      case "/agent-interaction":
        return <UserSound {...iconProps} />;
      case "/agent-interaction/blockcaller":
        return <PhoneSlash {...iconProps} />;
      case "/agent-interaction/prayer-partner-calllogs":
        return <VideoConference {...iconProps} />;
      case "/agent-interaction/callform":
        return <VideoConference {...iconProps} />;
      case "/pastoral-care":
        return <HandsPraying {...iconProps} />;
      case "/pastoral-care/pastoral-calllogs":
        return <FolderUser {...iconProps} />;
      case "/pastoral-care/completed-follow-ups":
        return <FolderStar {...iconProps} />;
      case "/pastoral-care/new-follow-ups":
        return <FolderPlus {...iconProps} />;
      case "/pastoral-care/ongoing-follow-ups":
        return <FolderDashed {...iconProps} />;
      case "/resources":
        return <Envelope {...iconProps} />;
      case "/resources/inventory":
        return <Equalizer {...iconProps} />;
      case "/resources/pastoral":
        return <Church {...iconProps} />;
      case "/resources/partner":
        return <UserRectangle {...iconProps} />;
      case "/resources/completed":
        return <SealCheck {...iconProps} />;
      case "/resources/returned":
        return <KeyReturn {...iconProps} />;
      case "/resources/fulfilled":
        return <Envelope {...iconProps} />;
      case "/resources/mailing-stats":
        return <ChartBarHorizontal {...iconProps} />;
      case "/requests":
        return <CalendarHeart {...iconProps} />;
      case "/requests/prayer-requests":
        return <Basket {...iconProps} />;
      case "/requests/voicemail":
        return <Voicemail {...iconProps} />;
      case "/subscribers":
        return <BuildingOffice {...iconProps} />;
      case "/testimony":
        return <Certificate {...iconProps} />;
      case "/followups":
        return <Notebook {...iconProps} />;
      case "/blockcaller":
        return <PhoneX {...iconProps} />;
      case "/pastoral-calllogs":
        return <HandHeart {...iconProps} />;
      case "/churches":
        return <Church {...iconProps} />;
      case "/prayer-partner-calllogs":
        return <HandsPraying {...iconProps} />;
      case "/invoices":
        return <Invoice {...iconProps} />;
      case "/prayer-requests":
        return <Basket {...iconProps} />;
      default:
        if (defaultPath !== "" && defaultPath !== path) {
          return getIcon(defaultPath);
        } else {
          return <House {...iconProps} />;
        }
    }
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    setOpenSubmenu(null);
    setOpenSubSubmenu(null);
    onToggle(!isOpen);
  };

  const handleSubmenuToggle = (path, event) => {
    if (openSubmenu === path) {
      setOpenSubmenu(null);
      setOpenSubSubmenu(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: isOpen ? rect.right + 10 : rect.right + 10,
      });
      setOpenSubmenu(path);
      setOpenSubSubmenu(null);
    }
  };

  const handleSubSubmenuToggle = (path, event) => {
    if (openSubSubmenu === path) {
      setOpenSubSubmenu(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubSubmenuPosition({
        top: rect.top,
        left: rect.right + 10,
      });
      setOpenSubSubmenu(path);
    }
  };

  // Helper function to check if a route has sub-routes
  const hasSubRoutes = (parentPath) => {
    return allowedRoutes.some(
      (r) =>
        r &&
        r.path &&
        !r.path.includes(":") && // Exclude routes with dynamic parameters
        r.path.startsWith(parentPath) &&
        r.path !== parentPath &&
        r.path.split("/").length === parentPath.split("/").length + 1,
    );
  };

  // Helper function to get sub-routes for a parent path
  const getSubRoutes = (parentPath) => {
    return allowedRoutes.filter(
      (r) =>
        r &&
        r.path &&
        !r.path.includes(":") && // Exclude routes with dynamic parameters
        r.path.startsWith(parentPath) &&
        r.path !== parentPath &&
        r.path.split("/").length === parentPath.split("/").length + 1,
    );
  };

  const renderMenuItem = (route) => {
    const isSubmenu = hasSubRoutes(route.path);
    const subRoutes = isSubmenu ? getSubRoutes(route.path) : [];
    const displayName = getLabel(route.path);
    const isActive = activePath.startsWith(route.path);
    const isNameClickedActive = nameClicked && route.path === "/subscribers";
    const iconElement = getIcon(route.path);
         return (
      <li key={route.path} className="menu-item-wrapper">
        {isSubmenu ? (
          <button
            onClick={(e) => handleSubmenuToggle(route.path, e)}
            className={`menu-item ${isActive ? "active" : ""} ${
              isNameClickedActive ? "active" : ""
            }`}
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={displayName}
            data-tooltip-place="right"
          >
            <span className="menu-icon">{iconElement}</span>
            {isOpen && (
              <>
                <span className="menu-label">{displayName}</span>
                <CaretRight size={16} className="submenu-arrow" />
              </>
            )}
          </button>
        ) : route.path === "/home" ? (
          <button
            onClick={handleHomeClick}
            className={`menu-item ${isActive ? "active" : ""}`}
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={displayName}
            data-tooltip-place="right"
          >
            <span className="menu-icon">{iconElement}</span>
            {isOpen && <span className="menu-label">{displayName}</span>}
          </button>
        ) : (
          <Link
            to={route.path}
            className={`menu-item ${isActive ? "active" : ""}`}
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={displayName}
            data-tooltip-place="right"
            onClick={() => {
              setOpenSubmenu(null);
              setOpenSubSubmenu(null);
            }}
          >
                            <span className="menu-icon">{iconElement}</span>
            {isOpen && <span className="menu-label">{displayName}</span>}
          </Link>
        )}

        {/* Floating Submenu */}
        {isSubmenu && openSubmenu === route.path && subRoutes.length > 0 && (
          <div
            ref={submenuRef}
            className="submenu-popup"
            style={{
              top: `${submenuPosition.top}px`,
              left: `${submenuPosition.left}px`,
            }}
          >
            <ul className="submenu-list">
              {subRoutes
                .filter(
                  (route) => route && route.path && !route.path.includes(":"),
                )
                .map((subRoute) => {
                  const subDisplayName = getLabel(subRoute.path);
                  const hasSubSubRoutes = hasSubRoutes(subRoute.path);
                  const subSubRoutes = hasSubSubRoutes
                    ? getSubRoutes(subRoute.path)
                    : [];

                  return (
                    <li key={subRoute.path} className="submenu-item-wrapper">
                      {hasSubSubRoutes ? (
                        <button
                          onClick={(e) =>
                            handleSubSubmenuToggle(subRoute.path, e)
                          }
                          className={`submenu-item ${
                            activePath.startsWith(subRoute.path) ? "active" : ""
                          }`}
                        >
                          <span className="submenu-icon">
                            {getIcon(subRoute.path, route.path)}
                          </span>
                          <span className="submenu-label">
                            {subDisplayName}
                          </span>
                          <CaretRight size={14} className="submenu-arrow" />
                        </button>
                      ) : (
                        <Link
                          to={subRoute.path}
                          className={`submenu-item ${
                            activePath === subRoute.path ? "active" : ""
                          }`}
                          onClick={() => {
                            setOpenSubmenu(null);
                            setOpenSubSubmenu(null);
                          }}
                        >
                          <span className="submenu-icon">
                            {getIcon(subRoute.path, route.path)}
                          </span>
                          <span className="submenu-label">
                            {subDisplayName}
                          </span>
                        </Link>
                      )}

                      {/* Sub-Sub Menu */}
                      {hasSubSubRoutes &&
                        openSubSubmenu === subRoute.path &&
                        subSubRoutes.length > 0 && (
                          <div
                            ref={subSubmenuRef}
                            className="submenu-popup sub-submenu-popup"
                            style={{
                              top: `${subSubmenuPosition.top}px`,
                              left: `${subSubmenuPosition.left}px`,
                            }}
                          >
                            <ul className="submenu-list">
                              {subSubRoutes
                                .filter(
                                  (route) =>
                                    route &&
                                    route.path &&
                                    !route.path.includes(":"),
                                )
                                .map((subSubRoute) => {
                                  const subSubDisplayName = getLabel(
                                    subSubRoute.path,
                                  );

                                  return (
                                    <li key={subSubRoute.path}>
                                      <Link
                                        to={subSubRoute.path}
                                        className={`submenu-item ${
                                          activePath === subSubRoute.path
                                            ? "active"
                                            : ""
                                        }`}
                                        onClick={() => {
                                          setOpenSubmenu(null);
                                          setOpenSubSubmenu(null);
                                        }}
                                      >
                                        <span className="submenu-icon">
                                          {getIcon(
                                            subSubRoute.path,
                                            subRoute.path,
                                          )}
                                        </span>
                                        <span className="submenu-label">
                                          {subSubDisplayName}
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                })}
                            </ul>
                          </div>
                        )}
                    </li>
                  );
                })}
            </ul>
          </div>
        )}
      </li>
    );
  };

  // Get only top-level routes (routes that don't have parent routes in the allowed list)
  const getTopLevelRoutes = () => {
    return allowedRoutes.filter((route) => {
      if (!route || !route.path) return false;
      const pathParts = route.path.split("/").filter(Boolean);

      // If it's a single segment path (like /home), it's top level
      if (pathParts.length === 1) return true;

      // Check if any parent path exists in allowedRoutes
      for (let i = 1; i < pathParts.length; i++) {
        const parentPath = "/" + pathParts.slice(0, i).join("/");
        if (allowedRoutes.some((r) => r && r.path === parentPath)) {
          return false; // This route has a parent, so it's not top level
        }
      }

      return true; // No parent found, it's top level
    });
  };

  return (
    <div ref={sidebarRef} className={`sidebar ${isOpen ? "open" : "closed"}`}>
      <div className="sidebar-header">
        {isOpen ? (
          <img
            src="https://brass-lynx-1229.twil.io/assets/A1P_Logo_ByCCCI-Dark.png"
            alt="A1P Logo"
            className="sidebar-logo"
          />
        ) : (
          <img
            src={require("../assets/images/a1pLogo.png")}
            alt="a1p Logo"
            className="sidebar-logo-small"
          />
        )}

        {isOpen && (
          <div className="sidebar-toggle-btn" onClick={toggleSidebar}>
            <img
              src={require("../assets/images/CaretCircleUpDown.png")}
              alt="Toggle"
            />
          </div>
        )}
      </div>

      {!isOpen && (
        <div className="sidebar-toggle-btn" onClick={toggleSidebar}>
          <img
            src={require("../assets/images/CaretCircleUpDown.png")}
            alt="Toggle"
          />
        </div>
      )}

      <nav className="sidebar-content">
        <ul className="menu-list">{getTopLevelRoutes().map(renderMenuItem)}</ul>
      </nav>

      <div className="sidebar-footer">
        <div
          className="user-profile"
          onClick={handleShow}
          data-tooltip-id="sidebar-tooltip"
          data-tooltip-content={`${userName || "User"} (${
            userRole ? userRole.replace("_", " ").toUpperCase() : "UNKNOWN"
          })`}
          data-tooltip-place="right"
        >
          <div className="user-avatar">
            {userImage ? (
              <img src={userImage} alt="User" />
            ) : (
              <span className="user-initials">
                {userName
                  ? userName
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                  : "U"}
              </span>
            )}
          </div>

          {isOpen && (
            <div className="user-info">
              <p className="user-name">{userName || "User"}</p>
              <p className="user-role">
                {userRole
                  ? userRole.replace("_", " ").toUpperCase()
                  : "UNKNOWN"}
              </p>
            </div>
          )}
        </div>

        <button
          onClick={handleLogout}
          className="logout-btn"
          data-tooltip-id="sidebar-tooltip"
          data-tooltip-content="Logout"
          data-tooltip-place="right"
        >
          <SignOut size={24} />
          {isOpen && <span className="logout-label">Logout</span>}
        </button>
      </div>

      {!isOpen && (
        <Tooltip
          id="sidebar-tooltip"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            color: "#fff",
            borderRadius: "4px",
            padding: "6px 10px",
            fontSize: "14px",
            zIndex: 9999,
          }}
        />
      )}
    </div>
  );
};

export default SideMenu;
